import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, CheckCircle } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { NATIONALITIES } from '../utils/nationalities';

export default function NationalityPicker({ value, onChange, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  // Find current nationality entry
  const selected = NATIONALITIES.find(n => 
    n.fr.toLowerCase() === (value || '').toLowerCase() ||
    n.ar === value
  );
  
  // Filter by search query (matches start of name OR contains)
  const filtered = useMemo(() => {
    if (!search.trim()) return NATIONALITIES;
    const q = search.toLowerCase().trim();
    const startsWith = NATIONALITIES.filter(n => 
      n.fr.toLowerCase().startsWith(q) || n.ar.startsWith(search.trim())
    );
    const contains = NATIONALITIES.filter(n => 
      !startsWith.includes(n) && (
        n.fr.toLowerCase().includes(q) || n.ar.includes(search.trim()) || n.code.toLowerCase().includes(q)
      )
    );
    return [...startsWith, ...contains];
  }, [search]);
  
  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);
  
  const selectNationality = (n) => {
    onChange(isAr ? n.ar : n.fr);
    setIsOpen(false);
    setSearch('');
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between gap-2 hover:border-blue-400 focus:ring-2 focus:ring-blue-500 transition ${
          isOpen ? 'border-blue-500 ring-2 ring-blue-200' : ''
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selected ? (
            <span className="text-2xl flex-shrink-0">{selected.flag}</span>
          ) : (
            <span className="text-2xl flex-shrink-0">🌍</span>
          )}
          <span className={`truncate ${value ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {selected 
              ? (isAr ? selected.ar : selected.fr)
              : (isAr ? 'اختر الجنسية...' : 'Choisir la nationalité...')}
          </span>
        </div>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="18" height="18" viewBox="0 0 24 24" 
          fill="none" stroke="currentColor" strokeWidth="2"
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden flex flex-col">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 start-2.5 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                placeholder={isAr ? 'بحث (مثلاً: م لـ مغربية)...' : 'Tapez la 1ère lettre (ex: F pour France)...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filtered.length > 0) {
                    e.preventDefault();
                    selectNationality(filtered[0]);
                  } else if (e.key === 'Escape') {
                    setIsOpen(false);
                    setSearch('');
                  }
                }}
                className="w-full ps-9 pe-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            {filtered.length > 0 && search && (
              <p className="text-xs text-gray-500 mt-1.5 px-1">
                {isAr ? `🔍 ${filtered.length} نتيجة` : `🔍 ${filtered.length} résultat(s)`}
              </p>
            )}
          </div>
          
          {/* List */}
          <div className="overflow-y-auto flex-1 max-h-64">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <div className="text-2xl mb-2">🔍</div>
                {isAr ? 'لا توجد نتائج' : 'Aucun résultat'}
              </div>
            ) : (
              filtered.map((n, idx) => (
                <button
                  key={n.code}
                  type="button"
                  onClick={() => selectNationality(n)}
                  className={`w-full px-3 py-2.5 text-start hover:bg-blue-50 active:bg-blue-100 transition flex items-center gap-3 ${
                    selected?.code === n.code ? 'bg-blue-50 border-s-4 border-blue-500' : 'border-s-4 border-transparent'
                  } ${idx === 0 && search ? 'ring-1 ring-inset ring-blue-200' : ''}`}
                >
                  <span className="text-2xl flex-shrink-0">{n.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {isAr ? n.ar : n.fr}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {isAr ? n.fr : n.ar}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 font-mono flex-shrink-0">{n.code}</span>
                  {selected?.code === n.code && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
